import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Box from "@mui/material/Box";
import Chip from '@mui/material/Chip';
import Autocomplete from "@mui/material/Autocomplete";
import Helpers from '../libraries/helpers';
import InputAdornment from '@mui/material/InputAdornment';
import { makeStyles, useTheme } from '@mui/styles';
import {
    isEmail,
    isURL,
    isIP,
    isIPRange,
    isPort,
    isLatLong,
    isHexadecimal,
} from 'validator';

// ... keep the existing typeChecks and other utility functions ...
const useStyles = makeStyles((theme) => ({
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      padding: theme.spacing(0.5),
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: 4,
      paddingRight: theme.spacing(1), // Space for text input
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      width: '100%', // Ensure the container takes full width
      minHeight: '40px', // Ensure enough height for chips
    },
    chips: {
      margin: theme.spacing(0.5),
    },
    errorText: {
        color: theme.palette.error.main, // Use theme's error color
        marginTop: theme.spacing(0.5), // Add some margin for spacing
    },
    validationError: {
        color: theme.palette.error.main, // Use theme's error color
        marginTop: theme.spacing(0.5), // Add some margin for spacing
    },
  }));

const typeChecks = {
    password: v => {
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&\-\+\_\#\^\.\,\;])[A-Za-z\d@$!%*?&\-\+\_\#\^\.\,\;]{8,}$/.test(v);
    },
    email: v => isEmail(v) && v.length < 255,
    hex: v => isHexadecimal(v),
    supportKey: v => isHexadecimal(v) && v.length == 20,
    host: v => {
        return (isURL(v, {
            require_protocol: false,
            require_tld: false,
            require_host: true
        }) && length < 255) || isIP(v, 4) || isIP(v, 6);
    },
    url: v => isURL(v, { require_protocol: true }),
    ipv4: v => isIP(v, 4),
    ipv6: v => isIP(v, 6),
    ipv4cidr: v => (isIPRange(v,4)),
    ipv6cidr: v => (isIPRange(v,6)),
    ip: v => (isIP(v, 4) || isIP(v, 6)),
    ipRange: v => (isIPRange(v) || isIP(v, 4) || isIP(v, 6)),
    ipList: v => {
        let i;
        let valid = true;
        const list = v.split(',');

        for (i = 0; i < list.length; i++) {
            if (!isURL(list[i].trim(), { require_protocol: false, require_tld: false, require_host: true })) {
                valid = false;
                break;
            }
        }

        return valid;
    },
    cidr: v => (isIPRange(v)),
    port: v => isPort(String(v)),
    latlon: v => isLatLong(v),
    sensorName: v => /^\w[a-zA-Z0-9_\-. (){}\[\]]{0,63}$/.test(v) && v.length < 64,
    policyName: v => /^[\w\u0041-\uffff](\w| |_|-|[\u0041-\uffff]){0,17}$/.test(v) && v.length < 18,
    exportName: v => /^\w[a-zA-Z0-9_\-. ]{0,31}$/.test(v) && v.length < 32,
    filterName: v => /^\w[a-zA-Z0-9_\-. (){}\[\]]{0,44}$/.test(v) && v.length < 45,
    snmpName: v => /^\w[a-zA-Z0-9_\-.]{1,64}$/.test(v) && v.length < 64,
    validNameCharacter: v => /^[\w\-\s]+$/.test(v) && v.length < 45,
    username: v => v.length < 32,
    displayName: v => v.length < 32,
    assetName: v => v.length > 0 && v.length < 42,
    kafkaTopicName: v => /^\w[a-zA-Z0-9_\-.]{0,255}$/.test(v),
    range: (v, min, max) => (parseInt(v) >= min && parseInt(v) <= max),
    length: (v, min) => (v.length >= min),
    // floating point range -1:1 e.g. "-0.3:0.3"
    pcr: v => {
        const vs = (v || '').split(':');
        if (vs.length !== 2) return false;
        const l = parseFloat(vs[0].trim());
        if (isNaN(l) || l < -1 || l > 1) return false;
        const r = parseFloat(vs[1].trim());
        if (isNaN(r) || r < -1 || r > 1) return false;
        return l <= r; // not sure about equals here
    },
    index: v => /^[a-z0-9]([a-z0-9_\-]{0,254}|)$/.test(v) && v.length < 255,
    portRange: v => {
        const portRange0to65535 = '([0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])';
        const portOptionalRangeOfRange = '\\s*' + portRange0to65535 + '(\\s*-\\s*' + portRange0to65535 + '\\s*)?';
        const portListOfRanges = '\\s*' + portOptionalRangeOfRange + '(\\s*,\\s*' + portOptionalRangeOfRange + '\\s*){0,}';
        const portRangeRegex = new RegExp('^' + portListOfRanges + '$');
        return portRangeRegex.test(v);
    },
    keyPairList: v => {
        v = Object.prototype.toString.call(v).includes('Array') ? v : v.split(',');
        return v.map(x => {
            const r = /^[a-zA-Z][a-zA-Z0-9\.-_]*[a-zA-Z0-9]\:[a-zA-Z][a-zA-Z0-9\.-_]*[a-zA-Z0-9]$/;
            const t = x.match(r);

            return !!t;
        }).reduce((a, c) => {
            if (!a || !c) {
                return false;
            } else {
                return true;
            }
        }, true);
    },
};


const MultipleInputs = forwardRef((props, ref) => {
    const {
        name,
        label,
        placeholder,
        valueType,
        allowEmpty,
        onChange,
        errorText,
        error,
        unique,
        value = [],
        excludes,
        excludeErrorText,
        ChipProps,
        renderInput,
        max,
        limit,
        ...rest
    } = props;

    const [inputValue, setInputValue] = useState(value || []);
    const [validationError, setValidationError] = useState('');
    const classes = useStyles();

    useEffect(() => {
        validateInput(inputValue);
    }, [inputValue]); // Trigger validation when inputValue changes

    // Validate input whenever the component mounts or updates with new value prop
    useEffect(() => {
        setInputValue(value || []); // Update local state if props change
        validateInput(value || []); // Validate initial value
    }, [value]); // Validate on value change

    const validateInput = (inputArray) => {
        let validationError = '';

        if (max && inputArray.length > max) {
            validationError = `You can only add up to ${max} tags.`;
        }

        if (!allowEmpty && inputArray.some((v) => v.trim() === '')) {
            validationError = 'Values cannot be empty.';
        }

        if (unique && inputArray.length !== new Set(inputArray).size) {
            validationError = 'Duplicate values are not allowed.';
        }

        if (excludes && excludes.length) {
            const invalidExcludes = inputArray.filter((v) => excludes.includes(v));
            if (invalidExcludes.length > 0) {
                validationError = excludeErrorText || 'Some values are not allowed.';
            }
        }

        inputArray.forEach((val) => {
            if (!typeChecks[valueType](val)) {
                validationError = `Invalid ${valueType}: ${val}`;
            }
        });

        setValidationError(validationError); // Always update validationError
        return validationError;
    };

    const handleChange = (event, newValue) => {
        // Prevent adding more tags if the limit is reached
        if (max && newValue.length > max) {
            return; // Early return to avoid updating inputValue
        }

        setInputValue(newValue);
        const validationError = validateInput(newValue);
        if (!validationError) {
            onChange && onChange(newValue);
        }
    };

    const handleDelete = (chipToDelete) => {
        setInputValue((prevValue) => {
            const updatedValue = prevValue.filter((chip) => chip !== chipToDelete);
            validateInput(updatedValue); // Validate after deletion
            onChange && onChange(updatedValue);
            return updatedValue;
        });
    };

    return (
        <Box >
            {renderInput ? (
                renderInput({ label, error, errorText, placeholder })
            ) : (
                <Autocomplete
                clearIcon={false}
                options={[]} // Placeholder for options
                freeSolo
                limitTags={limit}
                multiple
                value={inputValue}
                onChange={handleChange}
                renderTags={(value, props) =>
                    value.map((option, index) => (
                        <Chip
                            label={option}
                            {...props({ index })}
                            key={option}
                            onDelete={() => handleDelete(option)} // Handle chip deletion
                            {...ChipProps} // Spread ChipProps here
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={Boolean(error || validationError)} // Check both error props
                    helperText={(validationError || errorText)}// Show validation error or external error text
                />
                )}
            />
            )}
        </Box>
    );
});

// Define PropTypes for the MultipleInputs component
MultipleInputs.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    valueType: PropTypes.oneOf([
        'password',
        'email',
        'hex',
        'host',
        'url',
        'ipv4',
        'ipv6',
        'ipv4cidr',
        'ipv6cidr',
        'ip',
        'ipRange',
        'ipList',
        'cidr',
        'port',
        'portRange',
        'latlon',
        'sensorName',
        'policyName',
        'exportName',
        'filterName',
        'snmpName',
        'validNameCharacter',
        'username',
        'displayName',
        'assetName',
        'range',
        'length',
        'md5url',
        'pcr',
        'kafkaTopicName',
        'supportKey',
        'index',
        'keyPairList',
    ]).isRequired,
    allowEmpty: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    errorText: PropTypes.string,
    error: PropTypes.bool, 
    unique: PropTypes.bool,
    value: PropTypes.array,
    excludes: PropTypes.arrayOf(PropTypes.string),
    excludeErrorText: PropTypes.string,
    ChipProps: PropTypes.object,
    renderInput: PropTypes.func,
    max: PropTypes.number, 
    limit: PropTypes.number,
};

export default MultipleInputs;